import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface Post {
  id: string;
  title: string;
  date: string;
  author: string;
  category: string;
  excerpt: string;
  content: string;
  image?: string;
}

const blogPosts: Post[] = [
  {
    id: 'teaching-creative-writing-tips',
    title: '10 Tips for Teaching Creative Writing in the Classroom',
    date: 'January 8, 2025',
    author: 'PlotBuzz Team',
    category: 'Teaching Tips',
    excerpt: 'Practical strategies to help your students become confident writers.',
    content: `
      <h2>1. Start with Free Writing</h2>
      <p>Give students 5-10 minutes to write without worrying about grammar or structure. This removes barriers and helps ideas flow.</p>
      
      <h2>2. Use Writing Prompts</h2>
      <p>Prompts give students a starting point and reduce anxiety about "what to write about." Try creative scenarios like "You wake up with a superpower..." or "A mysterious package arrives..."</p>
      
      <h2>3. Model Your Own Writing</h2>
      <p>Share your writing process with students. Show them how you brainstorm, draft, and revise. This demystifies the writing process.</p>
      
      <h2>4. Create a Supportive Environment</h2>
      <p>Make your classroom a safe space for creative expression. Celebrate unique ideas and remind students that first drafts don't need to be perfect.</p>
      
      <h2>5. Teach Story Structure</h2>
      <p>Help students understand beginning, middle, and end. Introduce concepts like character development, setting, and conflict gradually.</p>
      
      <h2>6. Provide Regular Feedback</h2>
      <p>Give specific, constructive feedback that focuses on what's working well. Point out strong descriptions, interesting characters, or creative plot twists.</p>
      
      <h2>7. Use Peer Review</h2>
      <p>Have students share their work and provide feedback to each other. Teach them how to give constructive criticism.</p>
      
      <h2>8. Set Achievable Goals</h2>
      <p>Break writing projects into smaller milestones. Celebrate when students reach word count goals or complete chapters.</p>
      
      <h2>9. Incorporate Technology</h2>
      <p>Digital writing tools can engage reluctant writers. Platforms like PlotBuzz offer features like story planners and chapter organization that support the writing process.</p>
      
      <h2>10. Make Time for Sharing</h2>
      <p>Let students share their work through read-alouds or class publications. This gives writing a real audience and purpose.</p>
      
      <h2>Conclusion</h2>
      <p>Teaching creative writing is about fostering imagination while building skills. With these strategies, you can help every student find their voice as a writer.</p>
    `
  },
  {
    id: 'gamification-in-education',
    title: 'How Gamification Enhances Student Writing Engagement',
    date: 'January 7, 2025',
    author: 'PlotBuzz Team',
    category: 'EdTech',
    excerpt: 'Understanding the educational benefits of game-like elements in writing platforms.',
    content: `
      <h2>What is Gamification in Education?</h2>
      <p>Gamification applies game design elements—like points, badges, and progress tracking—to non-game contexts. In education, it motivates students and makes learning more engaging.</p>
      
      <h2>Why It Works for Writing</h2>
      <p>Writing can feel intimidating for many students. Gamification helps by:</p>
      <ul>
        <li><strong>Providing immediate feedback:</strong> Students see progress through word counts and XP points</li>
        <li><strong>Setting clear goals:</strong> Achievements and milestones give students targets to reach</li>
        <li><strong>Making practice fun:</strong> Daily challenges encourage consistent writing practice</li>
        <li><strong>Building confidence:</strong> Rewards celebrate small wins and progress</li>
      </ul>
      
      <h2>Educational vs. Entertainment Gaming</h2>
      <p>It's important to distinguish between educational gamification and entertainment gaming:</p>
      <p><strong>Educational gamification</strong> uses game elements to support learning objectives. The core activity is still educational—students are developing writing skills, receiving teacher feedback, and completing academic assignments.</p>
      <p><strong>Entertainment gaming</strong> focuses on play for enjoyment, often with multiplayer competition or fictional scenarios unrelated to learning.</p>
      
      <h2>Research-Backed Benefits</h2>
      <p>Studies show that gamification in education can:</p>
      <ul>
        <li>Increase student motivation by up to 40%</li>
        <li>Improve assignment completion rates</li>
        <li>Encourage more time spent on task</li>
        <li>Build growth mindset through achievement systems</li>
      </ul>
      
      <h2>Best Practices</h2>
      <p>Effective educational gamification should:</p>
      <ul>
        <li>Support learning goals, not distract from them</li>
        <li>Provide meaningful rewards tied to skill development</li>
        <li>Include teacher control and oversight</li>
        <li>Focus on progress and mastery, not just competition</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>When implemented thoughtfully, gamification is a powerful tool for engaging students in writing practice. It's not about turning education into a game—it's about making learning more motivating and rewarding.</p>
    `
  },
  {
    id: 'why-plotbuzz-is-educational',
    title: 'Why PlotBuzz is an Educational Platform, Not a Game',
    date: 'January 6, 2025',
    author: 'PlotBuzz Team',
    category: 'About PlotBuzz',
    excerpt: 'Addressing common misconceptions about our educational writing platform.',
    content: `
      <h2>The Confusion</h2>
      <p>Some web filters categorize PlotBuzz as a "gaming site" because we use motivational elements like XP points, achievements, and progress tracking. We understand the confusion, but PlotBuzz is fundamentally an educational tool designed for classroom writing instruction.</p>
      
      <h2>What PlotBuzz Actually Is</h2>
      <p>PlotBuzz is a K-12 creative writing platform with:</p>
      <ul>
        <li><strong>Teacher Dashboards:</strong> Educators manage classes, create assignments, and review student work</li>
        <li><strong>Writing Assignments:</strong> Teachers assign prompts with specific requirements and due dates</li>
        <li><strong>Feedback Tools:</strong> Teachers provide detailed comments and suggestions on student writing</li>
        <li><strong>Progress Tracking:</strong> Monitor word counts, completion rates, and writing activity</li>
        <li><strong>Classroom Management:</strong> Secure class codes, student rosters, and submission tracking</li>
      </ul>
      
      <h2>How Motivational Elements Support Learning</h2>
      <p>Yes, we use gamification—but it serves educational purposes:</p>
      <ul>
        <li><strong>XP Points:</strong> Track writing progress and encourage consistent practice</li>
        <li><strong>Achievements:</strong> Celebrate milestones like completing chapters or reaching word count goals</li>
        <li><strong>Streaks:</strong> Motivate daily writing habits</li>
        <li><strong>Challenges:</strong> Provide structured writing practice with specific goals</li>
      </ul>
      
      <p>These elements don't replace instruction—they enhance engagement with the core educational activity: writing.</p>
      
      <h2>What PlotBuzz Is NOT</h2>
      <ul>
        <li>No multiplayer gaming or competition between students</li>
        <li>No violent content or entertainment-focused scenarios</li>
        <li>No purchasing of virtual items or in-game currency</li>
        <li>No time-wasting mini-games unrelated to writing</li>
        <li>No distractions from core educational objectives</li>
      </ul>
      
      <h2>Educational Standards Alignment</h2>
      <p>PlotBuzz supports Common Core writing standards including:</p>
      <ul>
        <li>Narrative writing development</li>
        <li>Creative expression and voice</li>
        <li>Story structure and organization</li>
        <li>Drafting, revising, and editing processes</li>
        <li>Receiving and incorporating feedback</li>
      </ul>
      
      <h2>For IT Administrators</h2>
      <p>If your school's web filter is blocking PlotBuzz, please help us by requesting recategorization from "Gaming" to "Education." We're actively working with filtering services to correct this classification.</p>
      
      <p>For assistance with web filtering issues, contact us at support@plotbuzz.com</p>
      
      <h2>Conclusion</h2>
      <p>PlotBuzz uses engagement strategies from games, but it is not a game itself. Every feature exists to support one goal: helping students become better writers through structured practice, teacher guidance, and meaningful feedback.</p>
    `
  }
];

const BlogPost: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  
  const post = blogPosts.find(p => p.id === postId);
  
  if (!post) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
        <h1>Post not found</h1>
        <button onClick={() => navigate('/blog')}>← Back to Blog</button>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 40px'
      }}>
        <button
          onClick={() => navigate('/blog')}
          style={{
            background: 'none',
            border: 'none',
            color: '#6C5CE7',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back to Blog
        </button>
      </div>

      {/* Content */}
      <article style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 40px'
      }}>
        <div style={{
          background: '#6C5CE7',
          color: 'white',
          display: 'inline-block',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.85rem',
          fontWeight: '600',
          marginBottom: '20px'
        }}>
          {post.category}
        </div>

        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '800',
          color: '#333',
          marginBottom: '20px',
          lineHeight: '1.2'
        }}>
          {post.title}
        </h1>

        <div style={{
          display: 'flex',
          gap: '20px',
          fontSize: '0.9rem',
          color: '#666',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <span>By {post.author}</span>
          <span>•</span>
          <span>{post.date}</span>
        </div>

        <div
          style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: '#333'
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <style>{`
          article h2 {
            font-size: 1.8rem;
            font-weight: 700;
            color: #333;
            margin: 40px 0 20px;
          }
          article p {
            margin: 20px 0;
          }
          article ul {
            margin: 20px 0;
            padding-left: 30px;
          }
          article li {
            margin: 10px 0;
          }
          article strong {
            font-weight: 700;
            color: #6C5CE7;
          }
        `}</style>
      </article>
    </div>
  );
};

export default BlogPost;
export { blogPosts };